import * as React from "react"
import { Link } from "gatsby"
import Layout from "../components/Layout"
import Seo from "../components/SEO"
import Button from "../components/Button/Button"
import Faq from "../components/Faq/Faq"
import { OfferingStyles } from "../components/Offering/OfferingStyles"
import {
  CONTACT_EMAIL,
  included,
  process,
  needs,
  featurePrice,
  addOns,
  faqs,
} from "../components/Offering/offeringData"

const CustomWebsites = () => {
  return (
    <>
      <Seo title="Custom Websites" />
      <Layout>
        <OfferingStyles>
          {/* ── Hero ─────────────────────────────────────────── */}
          <section className="o-hero">
            <div className="o-hero__inner">
              <p className="eyebrow">Custom Websites</p>
              <h1>
                Custom websites, built from scratch
                <span className="accent">.</span>
              </h1>
              <p className="o-hero__lead">
                For performers, artists, and creatives — designed to make a
                strong first impression and look effortless on every screen.
                Never a template.
              </p>
              <p className="o-hero__who">
                Built for <b>performers</b>, <b>artists</b> &amp;{" "}
                <b>creatives</b>
              </p>
              <div className="o-hero__btns">
                <Button as={Link} to="/contact" text="Start your project" arrow />
                <Button href="#pricing" text="See pricing" ghost noArrow />
              </div>
            </div>
          </section>

          {/* ── What's included ──────────────────────────────── */}
          <section className="band band--coal">
            <div className="band__inner">
              <div className="band__head">
                <p className="eyebrow">What’s included</p>
                <h2>Everything you need, in every build.</h2>
                <p className="band__lead">
                  Every website comes with the essentials baked in — no upsells
                  for the basics.
                </p>
              </div>
              <div className="included">
                {included.map((item, index) => (
                  <div className="included__item" key={index}>
                    <span className="included__num">{`0${index + 1}`}</span>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Process ──────────────────────────────────────── */}
          <section className="band">
            <div className="band__inner">
              <div className="band__head">
                <p className="eyebrow">How it works</p>
                <h2>From first hello to launch day.</h2>
                <p className="band__lead">
                  A simple, four-step process designed to keep things clear and
                  stress-free.
                </p>
              </div>
              <div className="process">
                {process.map((step, index) => (
                  <div className="process__step" key={index}>
                    <div className="process__num">{`0${index + 1}`}</div>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── What we need from you ────────────────────────── */}
          <section className="band band--coal">
            <div className="band__inner">
              <div className="needs">
                <div className="band__head" style={{ marginBottom: 0 }}>
                  <p className="eyebrow">What we need from you</p>
                  <h2>A few things to get started.</h2>
                  <p className="band__lead">
                    The more you can share, the better — but don’t worry if
                    you’re still pulling it together.
                  </p>
                </div>
                <div>
                  <ul className="needs__list">
                    {needs.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <div className="reassure" style={{ marginTop: "var(--gap)" }}>
                    <p>Don’t have it all together yet?</p>
                    <p className="reassure__sub">
                      No problem — we’ll shape it as we go. Bring what you have
                      and we’ll build around it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Pricing ──────────────────────────────────────── */}
          <section className="band" id="pricing">
            <div className="band__inner">
              <div className="band__head">
                <p className="eyebrow">Pricing</p>
                <h2>Simple, transparent pricing.</h2>
                <p className="band__lead">
                  One flat rate for a complete custom website. Add only what you
                  need.
                </p>
              </div>

              <div className="pricing__feature">
                <div className="pricing__feature-main">
                  <h3>{featurePrice.title}</h3>
                  <p>{featurePrice.body}</p>
                </div>
                <div className="pricing__feature-side">
                  <span className="pricing__amount">{featurePrice.price}</span>
                  <Button
                    as={Link}
                    to="/contact"
                    text="Start your project"
                    arrow
                  />
                </div>
              </div>

              <div className="pricing__rows">
                {addOns.map((row, index) => (
                  <div className="pricing__row" key={index}>
                    <div className="pricing__row-main">
                      <h4>{row.title}</h4>
                      <p>{row.body}</p>
                    </div>
                    <div
                      className="pricing__row-price"
                      data-muted={row.price === "Not included"}
                    >
                      {row.price}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQs ─────────────────────────────────────────── */}
          <section className="band band--coal">
            <div className="band__inner">
              <div className="band__head">
                <p className="eyebrow">Questions</p>
                <h2>Good to know.</h2>
              </div>
              <div className="o-faqs">
                {faqs.map((item, index) => (
                  <Faq
                    key={index}
                    title={item.question}
                    description={item.answer}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* ── Closing CTA ──────────────────────────────────── */}
          <section className="band o-cta">
            <div className="o-cta__inner">
              <p className="eyebrow">Let’s talk</p>
              <h2>
                Ready to make a strong first impression
                <span className="accent">?</span>
              </h2>
              <p className="o-cta__lead">
                Tell us a little about you and your project — we’ll take it from
                there.
              </p>
              <div className="o-cta__btns">
                <Button as={Link} to="/contact" text="Start your project" arrow />
                <Button
                  href={`mailto:${CONTACT_EMAIL}`}
                  text="Email us"
                  ghost
                  noArrow
                />
              </div>
            </div>
          </section>
        </OfferingStyles>
      </Layout>
    </>
  )
}

export default CustomWebsites
